// Контракт ручки состава диагностического листа:
// `GET /v1/oge/diagnostic-sheet?skill=<слаг>&difficulty=<1..3>` (6.2
// `docs/student-errors-design.md`).
//
// ⚠️ **Ручка не принимает ученика ни одним параметром, и это свойство
// безопасности, а не экономия.** Состав листа это свойство графа и реестра
// генераторов, а не человека: то, что не принимает идентификатор ученика, не
// может им утечь. Отсюда же следует кэш на час: ответ меняется только выкаткой.
//
// ⚠️ Файл лежит в КОРНЕВОМ экспорте рядом с `./student-skills.ts` и обязан
// оставаться пустым от значений: схемы zod и типы, ноль импортов из `./oge/*`.
// Один импорт значением оттуда вытащил бы граф пререквизитов в стартовый чанк,
// то есть ровно то, ради чего состав считает сервер.
//
// ⚠️ Форма предметно-нейтральна (3.3 проекта): слаги это свободные строки,
// `subject` обязателен с первой версии, а объясняющая подпись приезжает полем
// `note`, а не литералом в компоненте. У русского порядок листа тот же, а слова
// другие.

import { z } from "zod";

/**
 * Строка листа: тема, уровень и количество заданий.
 *
 * Названия темы здесь нет намеренно: каждый пик это тема, по которой генераторы
 * ЕСТЬ, то есть она уже лежит в справочнике `/v1/homework/generator-topics`,
 * который у формы выдачи и так загружен и кэширован. У пропущенных тем
 * (`DiagnosticSheetSkip`) название своё, потому что их в справочнике нет.
 */
export const DiagnosticSheetPick = z.object({
  skill: z.string().min(1),
  difficulty: z.number().int().min(1).max(3),
  count: z.number().int().min(1),
  /**
   * 0 у названной темы, 1 и больше у пререквизитов.
   *
   * ⚠️ Порядок листа несёт массив `picks`, а `depth` объясняет этот порядок.
   * Поле нужно затем, чтобы предпросмотр мог отделить основы от названной темы,
   * не сличая слаги с `target` и не полагаясь на то, что тема ровно последняя.
   */
  depth: z.number().int().min(0),
});
export type DiagnosticSheetPick = z.infer<typeof DiagnosticSheetPick>;

/**
 * Пререквизит, не попавший в лист.
 *
 * ⚠️ Это НЕ отладка, а часть предпросмотра (6.2 проекта). «Свойства квадратных
 * корней в лист не вошли, заданий по ним нет» это то же честное сообщение, что
 * и блок «глубже не проверяли» в панели, только сказанное заранее.
 */
export const DiagnosticSheetSkip = z.object({
  skill: z.string().min(1),
  /** Название своё: темы без генераторов в справочнике формы нет вовсе. */
  name: z.string().min(1),
  /** Текст ребра графа, слово в слово: он написан для преподавателя. */
  why: z.string().min(1),
  /**
   * Почему не вошла.
   *
   * Значение сегодня ровно одно, и поле всё равно заведено словом, а не
   * подразумевается: клиент выбирает по нему текст строки, и второй причине
   * («покрыта, но не поместилась в потолок узлов») предстоит появиться ровно
   * здесь. Свести её к той же строке было бы враньём: «заданий по ней нет» и
   * «задания есть, но лист и так длинный» это разные сообщения.
   */
  reason: z.enum(["no-generators"]),
});
export type DiagnosticSheetSkip = z.infer<typeof DiagnosticSheetSkip>;

export const DiagnosticSheetTarget = z.object({
  skill: z.string().min(1),
  name: z.string().min(1),
});
export type DiagnosticSheetTarget = z.infer<typeof DiagnosticSheetTarget>;

export const DiagnosticSheetResponse = z.object({
  /** Предмет, сегодня всегда `oge-math`. Обязателен с первой версии (3.3). */
  subject: z.string().min(1),
  /** Тема, которую назвал преподаватель. В листе она идёт ПОСЛЕДНЕЙ. */
  target: DiagnosticSheetTarget,
  /**
   * Состав листа В ПОРЯДКЕ ЛИСТА: сначала самые глубокие узлы, названная тема
   * последней (4.1 проекта).
   *
   * ⚠️ Порядок это часть контракта, а не украшение. Он выбран на случай
   * брошенного листа: дорешанной должна оказаться диагностически ценная
   * половина, то есть основы. Пересортировать массив «для красоты» на любой из
   * двух сторон значит отменить это решение молча.
   *
   * Пустым не бывает: лист без единой строки это не лист, и ручка отказывает
   * раньше (`generator_unavailable`).
   */
  picks: z.array(DiagnosticSheetPick).min(1),
  skipped: z.array(DiagnosticSheetSkip),
  /**
   * Подпись предпросмотра, объясняющая порядок.
   *
   * ⚠️ Обязательна и не является украшением (4.1 проекта): лист «по квадратным
   * уравнениям» начинается с дробей, и без этой строки преподаватель решит, что
   * форма перепутала тему. Текстом с сервера, а не литералом в компоненте, по
   * той же причине, что `rootWord` в `./student-skills.ts`: у второго предмета
   * это правка контракта, а не строки в чужом файле.
   */
  note: z.string().min(1),
});
export type DiagnosticSheetResponse = z.infer<typeof DiagnosticSheetResponse>;
